import { useRecoilState } from 'recoil'
import { useAllCheckpointsState } from 'screens/Admin/ProjectCheckpoints/hooks/useAllCheckpointsState'
import { useProjectStagesState } from 'screens/Admin/ProjectTypes/hooks/useProjectStagesState'
import useProjectTypesState from 'screens/Admin/ProjectTypes/hooks/useProjectTypesState'
import { IProjectStage, IProjectType } from 'screens/Admin/ProjectTypes/projectTypes.types'
import knowleageBaseFiltersAtom from '../knowleageBaseFilters.atom'
import { useMemo } from 'react'

const useFaqFiltersAndOptionsState = () => {
  const { checkpointsMap } = useAllCheckpointsState()
  const { projectStagesMap } = useProjectStagesState()
  const { projectTypesMap, projectTypesState } = useProjectTypesState()

  const [state, setState] = useRecoilState(knowleageBaseFiltersAtom)

  const getProjectTypeCheckpoints = (projectTypeId: IProjectType['id']): string[] => {
    const projectType = projectTypesMap[projectTypeId]
    const projectStages = projectType?.projectStages.map((ps) => projectStagesMap[ps.projectStageId])
    return Array.from(new Set(projectStages?.flatMap((ps) => ps.checkpoints.map((c) => c.projectCheckpointId)) ?? []))
  }

  const handleChangeProjectType = (projectTypeId: IProjectType['id']) => {
    setState((prev) => ({
      ...prev,
      projectTypeId,
      projectStageId: null,
      checkpointIds: getProjectTypeCheckpoints(projectTypeId),
    }))
  }

  const handleChangeProjectStage = (projectStageId: IProjectStage['id'] | null) => {
    const projectStage = projectStagesMap[projectStageId]
    let checkpointIds: string[] = []
    if (projectStageId) {
      checkpointIds = projectStage?.checkpoints.map(c => c.projectCheckpointId) ?? []
    } else {
      checkpointIds = getProjectTypeCheckpoints(state.projectTypeId)
    }

    setState((prev) => ({
      ...prev,
      checkpointIds,
      projectStageId,
    }))
  }

  const handleChange = (val: any, name: string) => {
    switch (name) {
      case 'projectTypeId':
        return handleChangeProjectType(val as string | null)
      case 'projectStageId':
        return handleChangeProjectStage(val as string | null)
      default:
        return setState((prev) => ({
          ...prev,
          [name]: val,
        }))
    }
  }
  const checkpointsOptions = useMemo(() => {
    if (state.projectStageId) {
      const checkpointOptionsSet = new Set(
        projectStagesMap[state.projectStageId].checkpoints.map((c) => checkpointsMap[c.projectCheckpointId])
      )
      return Array.from(checkpointOptionsSet)
    }
    if (state.projectTypeId) {
      const checkpointOptionsSet = new Set(
        projectTypesMap[state.projectTypeId]?.projectStages?.flatMap((ps) =>
          projectStagesMap[ps.projectStageId]?.checkpoints.map((c) => checkpointsMap[c.projectCheckpointId])
        )
      )
      return Array.from(checkpointOptionsSet)
    }
    return []
  }, [state.projectStageId, state.projectTypeId])

  const projectStagesOptions =
    projectTypesMap[state.projectTypeId]?.projectStages.map((ps) => projectStagesMap[ps.projectStageId]) ?? []

  const hasFilters = state.checkpointIds.length > 0 || !!state.projectStageId || !!state.projectTypeId
  return {
    state: [state, handleChange] as const,
    hasFilters,
    checkpointsOptions,
    projectStagesOptions,
    projectTypesOptions: projectTypesState.items,
  }
}

export default useFaqFiltersAndOptionsState
